import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Loader2, X } from 'lucide-react';
import { Button } from './ui/button';

// Convert a File + crop area to a JPEG Blob using a canvas. No extra deps.
async function getCroppedBlob(file, area) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = area.width;
    canvas.height = area.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      img,
      area.x, area.y, area.width, area.height,
      0, 0, area.width, area.height,
    );
    return await new Promise((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.92),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Live circle preview of the cropped square — uses the same File source URL
// and CSS transforms to mirror the Cropper view, avoiding any per-keystroke
// canvas work.
function CirclePreview({ src, crop, zoom, area }) {
  // We use the cropped area to compute the visible window of the source image
  // inside a fixed 96×96 circular viewport.
  if (!area || !src) {
    return (
      <div className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs text-slate-400">
        Preview
      </div>
    );
  }
  return (
    <div
      className="w-24 h-24 rounded-full overflow-hidden border-2 border-[#16a34a] shadow-sm"
      style={{ background: '#fff' }}
      data-testid="headshot-crop-preview"
    >
      <img
        src={src}
        alt="Crop preview"
        style={{
          width: `${(96 * (crop.naturalWidth / area.width)) || 96}px`,
          height: `${(96 * (crop.naturalHeight / area.height)) || 96}px`,
          transform: `translate(${-(area.x / area.width) * 96}px, ${-(area.y / area.height) * 96}px)`,
          maxWidth: 'none',
          objectFit: 'cover',
        }}
      />
    </div>
  );
}

export default function HeadshotCropModal({ file, onConfirm, onCancel }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  // Build / clean up object URL from File. revokeObjectURL on unmount.
  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    // Read natural size to power the live preview transform
    const probe = new Image();
    probe.onload = () => setNaturalSize({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Esc closes the modal as Cancel; trap focus to confirm/cancel pair.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel?.();
      }
    };
    window.addEventListener('keydown', onKey);
    // Move initial focus to the primary CTA so Enter fires confirm.
    const t = setTimeout(() => confirmRef.current?.focus(), 50);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [onCancel, busy]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!file || !croppedAreaPixels || busy) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(file, croppedAreaPixels);
      onConfirm?.(blob);
    } catch (err) {
      // Fall through to onCancel-equivalent so the user can re-pick the file.
      // Surface a console error for debugging; the parent's toast will catch
      // any subsequent server-side rejection.
      // eslint-disable-next-line no-console
      console.error('Crop failed:', err);
      setBusy(false);
    }
  }, [file, croppedAreaPixels, busy, onConfirm]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !busy) onCancel?.();
  };

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Crop your headshot"
      onClick={handleBackdrop}
      data-testid="headshot-crop-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-[#092825]">Crop your headshot</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Drag to reposition. Pinch or scroll to zoom. The circle preview shows what your PDF avatar will look like.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Close"
            data-testid="headshot-crop-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 p-4 sm:p-6 overflow-y-auto">
          <div
            className="relative bg-slate-900 rounded-lg overflow-hidden"
            style={{ minHeight: 280, height: 'min(60vh, 440px)' }}
          >
            {imageUrl && (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                objectFit="contain"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          {/* Sidebar — preview + zoom slider */}
          <div className="flex md:flex-col items-center md:items-start gap-4 md:w-44">
            <div className="flex flex-col items-center md:items-start">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                PDF preview
              </span>
              <CirclePreview
                src={imageUrl}
                crop={{ naturalWidth: naturalSize.width, naturalHeight: naturalSize.height }}
                zoom={zoom}
                area={croppedAreaPixels}
              />
            </div>
            <div className="flex-1 md:w-full">
              <label htmlFor="headshot-zoom" className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Zoom
              </label>
              <input
                id="headshot-zoom"
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full accent-[#16a34a]"
                data-testid="headshot-crop-zoom"
                disabled={busy}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <Button
            type="button"
            variant="outline"
            ref={cancelRef}
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            data-testid="headshot-crop-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            ref={confirmRef}
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            className="bg-[#16a34a] hover:bg-[#0d7a36] text-white font-semibold"
            data-testid="headshot-crop-confirm-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading…
              </>
            ) : (
              'Use this photo'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
